import { HTMLAttributes, PropsWithChildren } from 'react'

import type {
  LeafMark as LeafMarkEnum,
  ListElementType as ListElementTypeEnum,
  ListItemElementType as ListItemElementTypeEnum,
  PlaceholderType as PlaceholderTypeEnum,
  PlaceholderValueType as PlaceholderValueTypeEnum,
  TextElementType as TextElementTypeEnum
} from '@mieterengel/database-types/types/enums'
import { BaseEditor } from 'slate'
import { ReactEditor } from 'slate-react'

export const TextElementType: Record<string, TextElementTypeEnum> = {
  Paragraph: 'Paragraph' as TextElementTypeEnum,
  HeadingOne: 'HeadingOne' as TextElementTypeEnum,
  HeadingTwo: 'HeadingTwo' as TextElementTypeEnum
}

export const ListElementType: Record<string, ListElementTypeEnum> = {
  NumberedList: 'NumberedList' as ListElementTypeEnum,
  BulletedList: 'BulletedList' as ListElementTypeEnum
}

export const ListItemElementType: Record<string, ListItemElementTypeEnum> = {
  ListItem: 'ListItem' as ListItemElementTypeEnum
}

export const LeafMark: Record<string, LeafMarkEnum> = {
  Bold: 'Bold' as LeafMarkEnum,
  Italic: 'Italic' as LeafMarkEnum,
  Underline: 'Underline' as LeafMarkEnum
}

export const PlaceholderType: Record<string, PlaceholderTypeEnum> = {
  Placeholder: 'Placeholder' as PlaceholderTypeEnum
}

export const PlaceholderValueType: Record<string, PlaceholderValueTypeEnum> = {
  string: 'string' as PlaceholderValueTypeEnum,
  text: 'text' as PlaceholderValueTypeEnum,
  integer: 'integer' as PlaceholderValueTypeEnum,
  float: 'float' as PlaceholderValueTypeEnum,
  email: 'email' as PlaceholderValueTypeEnum,
  phone: 'phone' as PlaceholderValueTypeEnum,
  date: 'date' as PlaceholderValueTypeEnum,
  datetime: 'datetime' as PlaceholderValueTypeEnum,
  enum: 'enum' as PlaceholderValueTypeEnum
}

export type LeafMark = 'Bold' | 'Italic' | 'Underline'

export type TextElement = {
  type: TextElementTypeEnum
  children: StructuredTextChild[]
}

export type ListItemElement = {
  type: ListItemElementTypeEnum
  children: StructuredTextChild[]
}

export type ListElement = {
  type: ListElementTypeEnum
  children: Array<ListItemElement>
}

export type PlaceholderElementBase = {
  type: PlaceholderTypeEnum
  parameterName: string
  placeholderText: string
  helpText?: string
  valueType: PlaceholderValueTypeEnum
  children: StructuredTextLeaf[]
}

export type PlaceholderElementEnum = PlaceholderElementBase & {
  valueType: PlaceholderValueTypeEnum.enum
  options: string[]
}

export type PlaceholderElement = PlaceholderElementBase | PlaceholderElementEnum

export type StructuredTextElement = TextElement | ListElement | ListItemElement

export type StructuredTextInlineElement = PlaceholderElement

export type StructuredTextChild = StructuredTextLeaf | PlaceholderElement

export type StructuredTextLeaf = { text: string } & Partial<Record<LeafMark, boolean>>

export type StructuredText = StructuredTextElement[]

export type ElementRenderProps<T extends StructuredTextElement | PlaceholderElement> =
  PropsWithChildren<{
    attributes: HTMLAttributes<any>
    element: T
  }>

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor
    Element: StructuredTextElement | StructuredTextInlineElement
    Text: StructuredTextLeaf
  }
}
